import { AUTO_LOGIN_STATUS, DECRYPT_KEY, LOCAL_STORAGE_KEY, LOGIN_TYPES, PAGE_LEVEL } from '@/constants';
import { MOBILEHOST, ROADSHOW_URL_HOST } from '@/apis/host';
import {
  buildMobileMaterialRedirectUrl,
  shouldRedirectMaterialToMobile
} from '@/pages/materialRedirect/mobileRedirect';
import { WORKBUDDY_LOGIN_STATUS, silentLoginFromWorkBuddy } from '@/utils/workBuddySilentLogin';
import { checkIdleSessionOnNavigation, refreshComplianceFlag } from '@/common/hooks/useIdleSessionTimeout';
import { checkLogin, cookie, getStorageAuthInfo, storage } from '@/utils/storageOperateApi';
/* eslint-disable prefer-const */
import { createRouter, createWebHistory } from 'vue-router';
import { decrypt, encrypt, getEnvInfoByKey, isEmptyObject, isH5, isOverseasEnv } from '@/utils/api';
import electron, { isElectron } from '@/utils/electron';
import { getCurrentConfig, isRouteInWhitelist } from '@/utils/version/versionConfig.js';
import { getHiddenFeatByOrg, getOriginMenuKeyToValue } from '@/utils/getPermissibleMenu';
import i18n, { loadLayoutLocale, loadLocaleModules } from '@/plugins/i18n';
import { isOpenedFromWorkBuddy, syncWorkBuddyHome, WORKBUDDY_HOME_APP } from '@/utils/embedEntry';
import slsLogger, { EVENT_TYPE } from '@/utils/slsLogger';

import { ElMessage } from 'element-plus';
// 版本检测相关
import VersionChecker from '@/utils/version/versionCheck.js';
import aiChatExternalRoutes from './routersWithoutAuth/aiChatExternal';
import aiChatShareRoutes from './routersWithoutAuth/aiChatShare';
import { emitter } from '@/plugins/emitter';
import { getRedirectUrl } from '@/utils/oversea';
import handleKeepAlive from './keepAlive';
import { initResearchWorkspaceApi } from '@/jmHttp/api/aiResearchMode';
import { initSandbox } from '@/jmHttp/api/openClaw';
import mcpAuthSuccessRoutes from './routersWithoutAuth/mcpAuthSuccess';
import { redirectPaths } from './redirect';
import researchDocumentShareRoutes from './routersWithoutAuth/researchDocumentShare';
import wafAntiDebugRoutes from './routersWithoutAuth/wafAntiDebug';

import routeCache from '@/utils/routeCache';
import routes from './route';
import store from '@/store';

const currentRedirectUrl = getRedirectUrl();

/**
 * 将带查询参数的重定向地址转换为 Vue Router 可识别的路由对象。
 * @param {string} redirectUri - 目标页面地址
 * @param {Record<string, unknown>} extraQuery - 额外需要透传的路由参数
 * @returns {{ path: string, query: Record<string, unknown> }} 路由跳转配置
 */
export const parseRedirectLocation = (redirectUri, extraQuery = {}) => {
  const targetUrl = new URL(redirectUri, window.location.origin);

  return {
    path: targetUrl.pathname,
    query: {
      ...Object.fromEntries(targetUrl.searchParams.entries()),
      ...extraQuery
    }
  };
};

// 版本检测器实例
let versionChecker = null;
let versionUpdateHandler = null;
let researchWorkspaceInitKey = '';

/** 免鉴权分享页：不触发需登录态的全局初始化（版本检测 / 工作区 init） */
const NO_AUTH_SHARE_ROUTE_NAMES = new Set([
  'aiChatShare',
  'aiChatIframe',
  'researchDocumentShare',
  'mcpAuthSuccess',
  'wafAntiDebug'
]);

/**
 * OAuth 独立页面（登录页 / 授权页）：与主业务共用 JM_AUTH_INFO 登录态，
 * 但不走菜单权限校验；未登录时由授权页跳转至 OAuth 登录页。
 */
const OAUTH_ROUTE_NAMES = new Set([
  'mcpOauthLogin',
  'mcpOauthAuthorize',
  'third-party-oauth-login',
  'third-party-oauth-authorize',
  'mcpOauthAuthorizeBase',
  'mcpOauthLoginBase'
]);

/** WorkBuddy OAuth 授权页：由该客户端打开却没有登录态的业务页统一先到这里换取登录态 */
const THIRD_PARTY_OAUTH_AUTHORIZE_PATH = '/workbuddy/oauth/authorize';

/**
 * 是否为业务页路由（既非通用登录页也非 OAuth 独立页）。
 *
 * 登录失效弹框跳登录页时不会带 redirect_uri，用失效前停留的业务页兜底，授权完成后才能把
 * 用户送回原来看的页面。
 * @param {import('vue-router').RouteLocationNormalized} route - 待判断的路由
 * @returns {boolean} 是业务页时返回 true
 */
const isBusinessRoute = (route) => !!route?.name && route.name !== 'login' && !OAUTH_ROUTE_NAMES.has(route.name);

/**
 * 初始化版本检测器
 */
const initVersionChecker = () => {
  if (versionChecker || !/\.comein\.cn/i.test(location.hostname.toLowerCase())) {
    return;
  }

  try {
    const config = getCurrentConfig();
    versionChecker = new VersionChecker({
      pollingInterval: config.pollingInterval,
      maxRetries: config.maxRetries,
      onUpdate: (updateInfo) => {
        if (versionUpdateHandler) {
          versionUpdateHandler(updateInfo);
        } else {
          emitter.emit('version-update-detected', updateInfo);
        }
      },
      onError: (error) => {
        logger.error('版本检测失败:', error);
      }
    });

    versionChecker.init();
  } catch (error) {
    logger.error('初始化版本检测器失败:', error);
  }
};

/**
 * 设置版本更新处理器
 */
const setVersionUpdateHandler = (handler) => {
  versionUpdateHandler = handler;
};

/**
 * 在路由变化时检测版本更新（包括首次进入）
 */
const checkVersionOnRoute = (to, from) => {
  // 只在有效的路由变化时检测
  if (!from.name || to.path === from.path) {
    return;
  }

  const isWhitelist = isRouteInWhitelist(to);

  setTimeout(() => {
    if (versionChecker) {
      versionChecker.checkOnRouteChange(isWhitelist);
    }
  }, 500);
};

// 暴露方法给外部使用
window.routerVersionCheck = {
  setHandler: setVersionUpdateHandler,
  getInstance: () => versionChecker,
  getStatus: () => versionChecker?.getStatus(),
  destroy: () => versionChecker?.destroy()
};

const redPaths = {};

for (const key in redirectPaths) {
  redPaths[redirectPaths[key]] = key;
}
const VUE_APP_MANAGE_PUBLIC_PATH = getEnvInfoByKey('VUE_APP_MANAGE_PUBLIC_PATH');

const findFirstMenuPath = (menus) => {
  if (menus.length === 0) return '';

  // 优先查找用户所设置的默认菜单
  let showMenu = menus.find((item) => item?.isDefaultRoute);
  if (!showMenu) {
    showMenu = menus[0];
  }
  return showMenu?.type === 'FUNCTION' ? showMenu?.childModuleList[0]?.code : showMenu?.code;
};
const route = [
  {
    path: '/',
    redirect: () => {
      const menus = getOriginMenuKeyToValue().menus;
      return findFirstMenuPath(menus) || currentRedirectUrl;
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import(/* webpackChunkName: 'login' */ '@/pages/login.vue'),
    meta: {
      title: 'common.noun.brm_login'
    }
  },
  // 强制清除用户信息
  {
    path: '/re-login',
    name: 'reLogin',
    component: () => import(/* webpackChunkName: 're-login' */ '@/pages/reLogin/index'),
    meta: {
      title: 'login',
      requireAuth: false
    }
  },
  // MCP OAuth 授权页：独立整页（无应用布局），需登录态；无任何菜单入口，仅通过链接跳转进入
  {
    path: '/mcp/oauth/authorize',
    name: 'mcpOauthAuthorize',
    component: () => import(/* webpackChunkName: 'mcp-oauth' */ '@/pages/mcpOauth/index.vue'),
    meta: {
      title: 'openClaw.mcpOauth.title',
      localeModules: ['openClaw']
    }
  },
  // MCP OAuth 登录页：独立整页，仅为授权页换取登录态（webtoken/uid），登录后携带 query 跳授权页
  {
    path: '/mcp/oauth/login',
    name: 'mcpOauthLogin',
    component: () => import(/* webpackChunkName: 'mcp-oauth' */ '@/pages/mcpOauth/login.vue'),
    meta: {
      title: 'openClaw.mcpOauth.login.title',
      localeModules: ['openClaw']
    }
  },
  // 第三方 OAuth 授权页：复用 MCP OAuth 页面结构，接口与登录回跳路径独立
  {
    path: '/workbuddy/oauth/authorize',
    name: 'third-party-oauth-authorize',
    component: () => import(/* webpackChunkName: 'third-party-oauth' */ '@/pages/thirdPartyOauth/index.vue'),
    meta: {
      title: 'openClaw.mcpOauth.title',
      localeModules: ['openClaw']
    }
  },
  // 第三方 OAuth 登录页：与授权页共用 WorkBuddy OAuth query
  {
    path: '/workbuddy/oauth/login',
    name: 'third-party-oauth-login',
    component: () => import(/* webpackChunkName: 'third-party-oauth' */ '@/pages/thirdPartyOauth/login.vue'),
    meta: {
      title: 'openClaw.mcpOauth.login.title',
      localeModules: ['openClaw']
    }
  },
  // MCP OAuth 授权页（Base 副本）：由 mcpOauth/index.vue 复制而来，独立整页（无应用布局），需登录态；无任何菜单入口
  {
    path: '/open/oauth/authorize',
    name: 'mcpOauthAuthorizeBase',
    component: () => import(/* webpackChunkName: 'mcp-oauth' */ '@/pages/mcpOauthBase/index.vue'),
    meta: {
      title: 'openClaw.mcpOauth.title',
      localeModules: ['openClaw']
    }
  },
  // MCP OAuth 登录页（Base 副本）：由 mcpOauth/login.vue 复制而来，登录后携带 query 跳回 Base 授权页
  {
    path: '/open/oauth/login',
    name: 'mcpOauthLoginBase',
    component: () => import(/* webpackChunkName: 'mcp-oauth' */ '@/pages/mcpOauthBase/login.vue'),
    meta: {
      title: 'openClaw.mcpOauth.login.title',
      localeModules: ['openClaw']
    }
  },
  {
    path: '/res',
    component: () => import(/* webpackChunkName: 'res' */ '@/pages/index.vue'),
    children: [
      ...routes,
      {
        path: 'not-found',
        name: 'notFound',
        component: () => import(/* webpackChunkName: "not-found" */ '@/pages/notFound/notFound.vue'),
        meta: {
          requireAuth: false
        }
      },
      {
        path: ':catchAll(.*)',
        redirect: 'not-found'
      }
    ]
  },
  {
    // electron 标签
    path: '/electron_refresh',
    component: null
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/res/not-found'
  },
  aiChatShareRoutes,
  aiChatExternalRoutes,
  researchDocumentShareRoutes,
  mcpAuthSuccessRoutes,
  wafAntiDebugRoutes
];
const router = createRouter({
  history: createWebHistory(getEnvInfoByKey('BASE_URL')),
  base: VUE_APP_MANAGE_PUBLIC_PATH ? `${VUE_APP_MANAGE_PUBLIC_PATH}/` : '',
  routes: route
});

// 是否初始化
let init = false;
// 布局语言模块是否已加载
let layoutLocaleLoaded = false;

const whitePath = [
  '/globalSearch/index',
  '/document/aiNote',
  '/voice/list',
  '/voice/detail',
  '/preprocessing/index',
  '/translation/index',
  '/translation/detail',
  '/aiNoteTask/index',
  '/aiChat/share',
  '/aiChat/iframe',
  '/research/document/share',
  '/thirdparty/externalAiChatIframe',
  '/thirdparty/summaryAuth',
  '/material/redirect'
];

// 校验公共子页面
const checkOtherParent = (menus, to) => {
  const otherParent = to.meta?.otherParent || [];
  for (let i = 0; i < otherParent.length; i++) if (menus[otherParent[i]]) return true;
  return false;
};
// electron环境中 在线程中获取用户信息 使用完需要清空
let ipcUserInfo = electron.sendSync('userInfo');

// 如果是手机或移动端访问，并且访问如下页面，则重定向到对应H5页面
/***
 * 纪要详情页
 * ***/
const redirectToH5 = (to) => {
  const h5SharePages = [
    // 云文档纪要详情
    {
      pageName: 'aiNoteCompare',
      source: 'ainote_detail'
    },
    {
      pageName: 'cloudIndex',
      source: 'cloud_index'
    },
    {
      pageName: 'cloudDetail',
      source: 'cloud_detail'
    }
  ];

  if (isH5 && !isElectron) {
    const page = h5SharePages.find((item) => item.pageName === to.name);
    if (page) {
      const url = `${MOBILEHOST}/preprocessing?stringifyQuery=${JSON.stringify(to.query)}&type=${page.source}`;
      window.location.href = url;
      return;
    }
  }
};

// 保存路由行为日志 包括合作机构登录等路由跳转
const syncSlsLogger = (to, from, extra = {}) => {
  slsLogger.behavior({
    action: EVENT_TYPE.PAGE_VIEW,
    target: {
      to: to.fullPath,
      from: from.fullPath
    },
    extra: {
      pageTitle:
        i18n.global.t(to.meta.title || '') ||
        i18n.global.t(to.meta.name || '') ||
        i18n.global.t('common.noun.come_in_title'),
      ...extra
    }
  });
};

/**
 * 首次进入项目时初始化当前登录用户的 Sandbox。
 * @param {import('vue-router').RouteLocationNormalized} to - 当前目标路由
 * @returns {void}
 */
const triggerSandboxInit = (to) => {
  slsLogger.console({
    action: 'router',
    message: '首次进入项目：初始化Sandbox',
    extra: { toPath: to.path, toName: to.name }
  });

  // 仅静默触发初始化，结果不影响导航；hideMessage 屏蔽接口异常的全局错误 toast（如沙盒服务异常）
  initSandbox(undefined, { hideMessage: true })
    .then(() => {
      slsLogger.console({
        action: 'router',
        message: '首次进入项目：初始化Sandbox成功',
        extra: { toPath: to.path, toName: to.name }
      });
    })
    .catch((error) => {
      slsLogger.console({
        action: 'router',
        message: '首次进入项目：初始化Sandbox失败',
        extra: { toPath: to.path, toName: to.name, errorMessage: error?.message || 'Unknown error' }
      });
    });
};

function schedulePostLoginTask(callback) {
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(callback, { timeout: 3000 });
    return;
  }
  window.setTimeout(callback, 0);
}

/**
 * 登录态下异步初始化研究模式工作区配置。
 * 兼容客户端 / 无感登录等非登录页链路；按 uid + 机构维度单页生命周期只触发一次。
 * @param {import('vue-router').RouteLocationNormalized} to - 当前目标路由
 */
const triggerResearchWorkspaceInit = (to) => {
  const jmAuthInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO) || {};
  const uid = jmAuthInfo.uid || jmAuthInfo.userinfo?.uid || jmAuthInfo.userinfo?.uuid || '';
  const orgId = jmAuthInfo.organizationId || jmAuthInfo.outsideOrgId || store.state.goId || '';
  if (!uid) return;

  const key = `${uid}:${orgId}`;
  if (researchWorkspaceInitKey === key) return;
  researchWorkspaceInitKey = key;

  schedulePostLoginTask(() => {
    initResearchWorkspaceApi({ hideMessage: true })
      .then(() => {
        slsLogger.console({
          action: 'router',
          message: '登录态初始化研究模式工作区配置成功',
          extra: { toPath: to.path, toName: to.name }
        });
      })
      .catch((error) => {
        slsLogger.console({
          action: 'router',
          level: 'error',
          message: '登录态初始化研究模式工作区配置失败',
          extra: { toPath: to.path, toName: to.name, errorMessage: error?.message || 'Unknown error' }
        });
      });
  });
};

/**
 * WorkBuddy 客户端停留在登录页时，用 wb.login 续上当前客户端账号的会话。
 *
 * 会话失效、空闲踢出、主动退出都会被动落到登录页，客户端里没有账号密码登录的入口，
 * 因此统一复用静默登录链路重新对齐 WorkBuddy 当前账号。
 * @param {import('vue-router').RouteLocationNormalized} to - 目标登录页路由
 * @param {string} [redirectUri] - 失效前的页面地址，续上登录态后回到该页
 * @returns {Promise<object|string|null>} WorkBuddy 会话返回改派目标，非 WorkBuddy 会话返回 null
 */
const resolveWorkBuddyLoginPageTarget = async (to, redirectUri) => {
  if (!isOpenedFromWorkBuddy(to.query)) return null;

  slsLogger.console({
    action: 'router',
    message: 'WorkBuddy 登录页自动登录：走 wb.login 续登录态',
    extra: { toPath: to.path, redirectUri }
  });
  const workBuddyLoginStatus = await silentLoginFromWorkBuddy();
  // 已续上登录态：回到失效前的页面，没有来源时落到默认菜单
  if (workBuddyLoginStatus === WORKBUDDY_LOGIN_STATUS.LOGGED_IN) {
    const menus = getOriginMenuKeyToValue().menus;
    return redirectUri || findFirstMenuPath(menus) || currentRedirectUrl;
  }
  // 未授权、未绑定 comein 账号，或链路异常（JSAPI 未就绪、接口失败、静默登录已试过一次）：
  // 客户端里没有账号密码登录入口，停在通用登录页用户什么也做不了，一律回授权页，由其重新
  // 取码授权，并在需要选账号时转 OAuth 登录页。
  return { path: THIRD_PARTY_OAUTH_AUTHORIZE_PATH, query: redirectUri ? { redirect_uri: redirectUri } : {} };
};

router.beforeEach(async (to, from, next) => {
  // 资料中转：手机访问必须在登录/无感登录/空闲踢出之前 replace 到移动端，PC cookie 带不过去。
  if (shouldRedirectMaterialToMobile(to, { isH5, isElectron })) {
    window.location.replace(buildMobileMaterialRedirectUrl({ mobileHost: MOBILEHOST, query: to.query }));
    return next(false);
  }
  // 合规受限用户空闲校验（生产 30 分钟 / 测试 5 分钟）：路由跳转视为「使用」，超时则触发踢出并中止本次导航
  if (checkIdleSessionOnNavigation(to)) {
    return next(false);
  }
  // 在登录与重定向分支之前保存 WorkBuddy 首次入口，避免提前 next() 导致首页目标丢失。
  const openedFromWorkBuddy = isOpenedFromWorkBuddy(to.query);
  syncWorkBuddyHome(to.query, openedFromWorkBuddy, {
    app: WORKBUDDY_HOME_APP.BRM,
    path: to.fullPath
  });
  // IRM 公司信息初始化（仅数据处理，不执行 next，不影响原有导航逻辑）
  if (to.query.source === 'irm' && to.query.irmCompanyInfo) {
    try {
      const info = JSON.parse(decrypt(decodeURIComponent(to.query.irmCompanyInfo), DECRYPT_KEY));
      store.commit('setIrmCompanyInfo', info);
      storage.set(LOCAL_STORAGE_KEY.IRM_COMPANY_INFO, info, '', true);
    } catch (e) {
      logger.log('irmCompanyInfo 解密失败', e);
    }
  }
  // 任意路由：store 为空时从 storage 恢复（保证刷新后数据不丢）
  if (!store.state.irmCompanyInfo?.irmCompanyId) {
    const cached = storage.get(LOCAL_STORAGE_KEY.IRM_COMPANY_INFO, true);
    if (cached) store.commit('setIrmCompanyInfo', cached);
  }

  if (!['summaryList', 'roadshowMeeting'].includes(to.name) && getHiddenFeatByOrg()) {
    return next({ name: 'summaryList' });
  }

  // 按需加载当前路由所需的语言模块（从 matched 中收集所有层级的 localeModules）
  const localeModules = to.matched.flatMap((record) => record.meta.localeModules || []);
  if (localeModules.length > 0) {
    await loadLocaleModules(i18n.global.locale.value, localeModules);
  }

  // 用户已登录时，加载主布局语言模块（只加载一次）
  if (!layoutLocaleLoaded && checkLogin()) {
    layoutLocaleLoaded = true;
    loadLayoutLocale(i18n.global.locale.value);
  }

  // 记录路由变化
  syncSlsLogger(to, from);

  //跳转mobile
  redirectToH5(to);

  if (isElectron)
    logger.log(
      `${JSON.stringify({
        fullPath: from.fullPath,
        meta: from.meta,
        name: from.name
      })} -> ${JSON.stringify({
        fullPath: to.fullPath,
        meta: to.meta,
        name: to.name
      })}`
    );

  if (!to.meta.dynamicTitle) {
    // 只有当 meta.dynamicTitle 不存在时，才设置默认标题
    document.title =
      i18n.global.t(to.meta.title || '') ||
      i18n.global.t(to.meta.name || '') ||
      i18n.global.t('common.noun.come_in_title');
  }

  if (+to.query.autoLogin === AUTO_LOGIN_STATUS.NO_LOGIN) {
    await store.dispatch('logoutAction');
  }

  // 老版本对接userChannel 新版本改为comein_channel
  let {
    userInfo,
    comein_channel,
    userSecretKey,
    options = {},
    userChannel,
    thirdparty,
    hideMenu,
    brmChannel,
    irmCompanyInfo: _irmCompanyInfo,
    ...args
  } = to.query;
  userChannel = userChannel || comein_channel;
  const { isThirdParty } = router.app.config.globalProperties?.$bridge || { thirdparty };

  // 判断是否是合作机构（被嵌入）
  if (userChannel && userInfo && isThirdParty) {
    // 判断是否已经合作机构登录状态中
    const isRefreshToken = await store.dispatch('checkThirdPartyTokenAction', to.query);
    if (isRefreshToken) {
      try {
        // Portal 直通鉴权：portal 与 BRM 共享用户体系，userInfo 中已包含有效 token，
        // 无需调用免登录换 token 接口，直接用 portal token 换取 BRM 鉴权信息
        if (to.query.source === 'roadshow') {
          const decoded = JSON.parse(decrypt(decodeURIComponent(userInfo), DECRYPT_KEY));
          const { go_id, token, uid, uc } = decoded;

          await store.dispatch('fetchUserTokenAndMenuAction', { goId: go_id, token, uid });

          const jmUserInfo = await store.dispatch('jm/getUserInfoByToken', { token });
          const jmAuthInfo = { ...decoded, webtoken: token, ...(jmUserInfo?.data || {}), uc: uc || userChannel };
          jmUserInfo?.data?.avatarurl && store.commit('setUserAvatar', jmUserInfo.data.avatarurl);
          storage.set(LOCAL_STORAGE_KEY.JM_AUTH_INFO, jmAuthInfo, '', true);
          store.commit('jm/initState', jmAuthInfo);
        } else {
          // 标准第三方合作机构鉴权（IRM、东方证券等）：通过免登录接口换取双 token
          const info = await store.dispatch('getAllTokenByUrlData', { userInfo, userChannel, fetchMenus: true });
          const { data } = info;

          // 判断是否是 进门帐号和企业版都没有注册
          if (!data.tokenComeBrm && !data.tokenComeIn) {
            return next({ name: 'notOrg' });
          }

          // 判断是否是 进门帐号有，企业版没有注册
          if (!data.tokenComeBrm && data.tokenComeIn) {
            const userInfo = encodeURIComponent(encrypt(JSON.stringify({ token: data.tokenComeIn }), DECRYPT_KEY));
            return (location.href = `${ROADSHOW_URL_HOST}/home/index?userInfo=${userInfo}`);
          }
        }
        storage.set(
          LOCAL_STORAGE_KEY.COOPERATION,
          { brmChannel: router.app.config.globalProperties?.$bridge?.comein_channel, userInfo, userChannel, hideMenu },
          '',
          true
        );
        try {
          sessionStorage.setItem('THIRD_PARTY_SESSION', '1');
        } catch {
          /* sessionStorage 不可用时忽略 */
        }
        // 发布登陆成功事件
        emitter.emit('login-success');
      } catch (error) {
        // 未注册 1992  企业版未注册 2030
        if (error && [1992, 2030].includes(+error?.code)) {
          return next({ name: 'notOrg' });
        }
        syncSlsLogger(to, from, {
          errorMessage: error?.message || 'Unknown error'
        });
        return next({ name: 'instituteNotFound' });
      }
    } else {
      storage.set(
        LOCAL_STORAGE_KEY.COOPERATION,
        { brmChannel: router.app.config.globalProperties?.$bridge?.comein_channel, userInfo, userChannel, hideMenu },
        '',
        true
      );
      try {
        sessionStorage.setItem('THIRD_PARTY_SESSION', '1');
      } catch {
        /* sessionStorage 不可用时忽略 */
      }
    }

    //输入brmChannel跟comein_channel不匹配时，更新brmChannel
    let thirdBrmChannel = { brmChannel };
    if (brmChannel && router.app.config.globalProperties?.$bridge?.comein_channel !== brmChannel) {
      thirdBrmChannel = {
        brmChannel: router.app.config.globalProperties?.$bridge?.comein_channel
      };
    }

    const params = typeof options !== 'string' ? options : JSON.parse(options);
    return next({
      name: to.name,
      query: { ...thirdBrmChannel, ...(args || {}), ...params }
    });
  }

  // 第三方缓存残留检测：bridge 从本地 COOPERATION 缓存判定为第三方，
  // 但当前访问没有第三方 URL 参数且不在当前标签页的第三方会话中，
  // 说明用户已离开第三方系统后直接访问了项目页面，需清理残留并按清理后的身份重载
  //
  // 注意两种存储的作用域差异：COOPERATION 在 localStorage（跨标签页共享），而第三方会话标记在
  // sessionStorage（每个标签页独立），新标签页天然读不到标记。因此必须把「本来就属于第三方
  // 或免登录」的目标路由排除在外，否则第三方页面 / 分享页 / OAuth 页在新标签打开时会被误判为残留。
  let hasCooperationCache = storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
  let isThirdPartySessionActive = false;
  try {
    isThirdPartySessionActive = !!sessionStorage.getItem('THIRD_PARTY_SESSION');
  } catch {
    /* sessionStorage 不可用时视为无活跃会话 */
  }
  // normal 渠道是进门内部账号的通用模式（isGeneralMode），并非真正的第三方嵌入，不参与残留检测
  const isRealThirdPartyChannel = isThirdParty && router.app.config.globalProperties?.$bridge?.thirdparty !== 'normal';
  if (
    isRealThirdPartyChannel &&
    hasCooperationCache &&
    !userChannel &&
    !userInfo &&
    !isThirdPartySessionActive &&
    !to.meta.thirdPartyPage &&
    !NO_AUTH_SHARE_ROUTE_NAMES.has(to.name) &&
    !OAUTH_ROUTE_NAMES.has(to.name) &&
    to.name !== 'login' &&
    to.name !== 'instituteNotFound'
  ) {
    // 只移除合作标识：残留只影响渠道判定，其余登录态有效，不应把用户一并登出
    storage.remove(LOCAL_STORAGE_KEY.COOPERATION);
    // 复查删除结果：残留仍在时整页重载会陷入死循环，此时只记录日志并放行本次导航
    const residualCleared = !storage.get(LOCAL_STORAGE_KEY.COOPERATION, true);
    slsLogger.console({
      action: 'router',
      message: residualCleared
        ? '第三方缓存残留检测：已清理合作登录缓存并重载'
        : '第三方缓存残留检测：合作登录缓存清理失败，跳过重载',
      extra: { toPath: to.path, brmChannel: hasCooperationCache.brmChannel, residualCleared }
    });
    if (residualCleared) {
      // $bridge.isThirdParty 与 window.comeinChannel 都在模块加载期算好，只有整页重载才会按清理后的
      // 缓存重新判定；重载到本次导航的目标地址，避免登录态仍有效的用户被无谓地绕去登录页
      delete window.comeinChannel;
      location.replace(to.fullPath);
      return;
    }
  }

  // Electron 客户端 COOPERATION 残留自愈：
  // 桌面 BRM 客户端顶层窗口自身永远不是被第三方嵌入的合作页（isThirdParty 恒为 false），
  // 但内嵌 portal 路演详情时，portal 会二次创建与客户端同源的 BRM /login 隐藏 iframe，
  // 其 getCooperateInfo 握手（见 App.vue）会把 COOPERATION 写入与客户端共享的 localStorage，形成残留；
  // 该残留既不会被上方第三方残留检测（受 isThirdParty 限制）清理，也不会被重新登录清理（actions.js 存在 COOPERATION 时跳过 localStorage.clear），
  // 最终触发合作硬跳转与登录死循环。故在真实 Electron 顶层客户端且非第三方上下文时主动清除该残留。
  if (
    isElectron &&
    window.self === window.top &&
    !isThirdParty &&
    hasCooperationCache &&
    !userChannel &&
    !userInfo &&
    !isThirdPartySessionActive
  ) {
    slsLogger.console({
      action: 'router',
      message: 'Electron 客户端清理残留 COOPERATION 缓存',
      extra: { toPath: to.path, brmChannel: hasCooperationCache?.brmChannel }
    });
    storage.remove(LOCAL_STORAGE_KEY.COOPERATION);
    // 同步置空本地引用，避免下方“合作标识同步”块因读取到残留而重新写回外壳
    hasCooperationCache = null;
  }

  // 判断本地是否合作标识不一致
  const comeinChannel = router.app.config.globalProperties?.$bridge?.comein_channel;
  const localBrmChannel = hasCooperationCache?.brmChannel;
  if (comeinChannel !== localBrmChannel) {
    storage.set(
      LOCAL_STORAGE_KEY.COOPERATION,
      { ...(storage.get(LOCAL_STORAGE_KEY.COOPERATION, true) || {}), brmChannel: comeinChannel },
      '',
      true
    );
  }

  // 携带 未认证brmChannel
  if (
    ((userChannel && userInfo && !isThirdParty) ||
      (storage.get(LOCAL_STORAGE_KEY.COOPERATION, true)?.brmChannel && !isThirdParty)) &&
    to.name !== 'instituteNotFound' &&
    userChannel !== 'brm'
  ) {
    storage.clear();
    return next({ name: 'instituteNotFound' });
  }

  if (NO_AUTH_SHARE_ROUTE_NAMES.has(to.name)) {
    // 移动端（含 App 内嵌 WebView）在路由层拦截，直接跳转 H5 分享页
    // 避免 PC 页面的 CMI/WAF SDK 初始化在 App 内报错
    if (to.name === 'aiChatShare' && isH5) {
      const shareSessionId = to.query.shareSessionId || '';
      window.location.replace(`${MOBILEHOST}/brm-mobile/aichat-share?shareSessionId=${shareSessionId}`);
      return;
    }
    return next();
  }

  // OAuth 页面直接放行，由页面基于共用 JM_AUTH_INFO 判断登录态
  if (OAUTH_ROUTE_NAMES.has(to.name)) {
    return next();
  }

  if (to.meta.thirdPartyPage && (window?.$bridge?.isThirdParty || storage.get(LOCAL_STORAGE_KEY.COOPERATION, true))) {
    // 合作-》路演-》合作纪要
    // 合作机构页面
    // 不需要登录
    if (to.meta.requireAuth === false) return next();

    const authUserInfo = getStorageAuthInfo();
    if (!authUserInfo) return next({ name: 'instituteNotFound' });
    const { brmToken } = authUserInfo;

    if ((!brmToken || isEmptyObject(brmToken)) && to.name !== 'instituteNotFound')
      return next({ name: 'instituteNotFound' });

    return next();
  }

  // url 携带无感登录数据
  if (userSecretKey && userChannel) {
    let path = currentRedirectUrl;
    try {
      const result = await store.dispatch('getAllTokenByUrlData', {
        userInfo: userSecretKey,
        userChannel,
        fetchMenus: true
      });
      const { menus } = result.authInfo;
      path = findFirstMenuPath(menus);
      // 发布登陆成功事件
      emitter.emit('login-success');
      slsLogger.console({
        action: 'router',
        message: '无感登录：URL userSecretKey 拉取全量鉴权成功',
        extra: { toPath: to.path, toName: to.name }
      });
    } catch (error) {
      logger.log('无感登录：URL userSecretKey 拉取全量鉴权失败', error);
      return next({ name: 'login', query: to.name === 'login' ? {} : { redirect_uri: to.fullPath || to.path } });
    }
    // 优先使用 to.query.redirect_uri，如果没有则使用 from.query.redirect_uri
    const redirectPath = to.query.redirect_uri || from.query.redirect_uri || path || currentRedirectUrl;
    return next(redirectPath);
  }

  // 优先使用 to.query.redirect_uri（当前路由的 redirect_uri），如果没有则使用 from.query.redirect_uri
  const redirect_uri = to.query.redirect_uri || from.query?.redirect_uri;

  // 判断是否在electron中能获取用户信息
  // url 携带参数
  if ((to.query.userInfo && userChannel !== 'brm') || (isElectron && ipcUserInfo)) {
    // 校验是否是进门账号登录
    try {
      let { userInfo } = to.query || {};
      userInfo = userInfo ? decodeURIComponent(userInfo) : ipcUserInfo;
      let params = {};
      if (isElectron) {
        let ipcUserInfoTmp = ipcUserInfo;
        if (typeof ipcUserInfoTmp === 'string') ipcUserInfoTmp = JSON.parse(ipcUserInfoTmp);
        params =
          ipcUserInfoTmp && (ipcUserInfoTmp.outsideOrgId || ipcUserInfoTmp?.userLoginIdentity?.outsideOrgId)
            ? ipcUserInfo
            : JSON.parse(decrypt(userInfo, DECRYPT_KEY));
      } else {
        params = ipcUserInfo ? ipcUserInfo : JSON.parse(decrypt(userInfo, DECRYPT_KEY));
      }
      if (typeof params === 'string') params = JSON.parse(params);
      const { go_id, token, uid, uc, userLoginIdentity } = params;

      const outsideOrgId = userLoginIdentity?.outsideOrgId || params.outsideOrgId;

      let brmAuthInfo = null;
      if (params?.brmAuthInfo) {
        brmAuthInfo = params?.brmAuthInfo;
      } else {
        brmAuthInfo = await store.dispatch('fetchUserTokenAndMenuAction', {
          goId: go_id || outsideOrgId,
          token,
          uid
        });
      }
      const { menus } = brmAuthInfo;

      // 获取进门用户信息
      let jmUserInfo = null;
      if (params?.jmUserInfo) {
        jmUserInfo = params?.jmUserInfo;
      } else {
        jmUserInfo = await store.dispatch('jm/getUserInfoByToken', { token });
      }
      jmUserInfo?.data?.avatarurl && store.commit('setUserAvatar', jmUserInfo?.data?.avatarurl);
      const allParams = {
        ...params,
        webtoken: token,
        ...jmUserInfo?.data,
        uc
      };
      const jmAuthInfo = params?.jmAuthInfo ? params.jmAuthInfo : allParams;

      storage.set(LOCAL_STORAGE_KEY.LOGIN_TYPE, LOGIN_TYPES.JM);
      storage.set(LOCAL_STORAGE_KEY.JM_AUTH_INFO, jmAuthInfo, '', true);
      store.commit('jm/initState', jmAuthInfo);
      slsLogger.console({
        action: 'router',
        message: '无感登录：URL/Electron userInfo 写入 JM_AUTH_INFO',
        extra: { toPath: to.path, toName: to.name, isElectron }
      });
      // 与 getUserCustomMenu 一致：显式传入本次无感登录 token/uid，避免读到 storage 旧 webtoken
      refreshComplianceFlag({ token, uid });

      const path = findFirstMenuPath(menus);
      window.parent && window.parent.postMessage({ event: 'author_success' }, '*');

      const route_redirect = to.query.redirect_uri; // 路由中存在重定向路径
      const route_queryString = to.query.queryString ? JSON.parse(to.query.queryString) : {}; // 路由中存在需要传递的路由参数

      const _path = redirect_uri || route_redirect || path || currentRedirectUrl;

      // 仅 Web 第三方嵌入场景需要 location.href 硬刷新以重建第三方上下文；
      // Electron 客户端若命中残留 COOPERATION 缓存也走硬刷新，会因主进程 userInfo 未清空，
      // 刷新后再次命中无感登录分支，形成登录后白屏死循环，故此处排除 Electron，
      // 改走下方 SPA 导航（会回写主进程 userInfo 并置空 ipcUserInfo，避免重复命中）。
      if (!isElectron && storage.get(LOCAL_STORAGE_KEY.COOPERATION, true))
        return (location.href = `${location.origin}${_path}`);
      const pathConfig = parseRedirectLocation(_path, route_queryString);
      electron.sendSync(
        'userInfo',
        JSON.parse(
          JSON.stringify({
            ...ipcUserInfo,
            brmAuthInfo,
            jmAuthInfo,
            jmUserInfo
          })
        )
      );
      ipcUserInfo = null;
      return next(pathConfig);
    } catch (error) {
      logger.log('用户信息获取失败，请重新登录', error);
      slsLogger.console({
        action: 'router',
        message: '用户信息获取失败',
        extra: {
          userInfo: to.query.userInfo,
          isElectron,
          ipcUserInfo,
          userChannel,
          errorMessage: error.message
        }
      });
      ElMessage.error('用户信息获取失败，请重新登录');

      // 置空 ipcUserInfo，避免 Electron 下 (isElectron && ipcUserInfo) 在跳转 login 后再次命中本分支，
      // 导致失败接口（如 getUserCustomMenu 500）被反复调用形成死循环
      ipcUserInfo = null;

      return next({ name: 'login', query: to.name === 'login' ? {} : { redirect_uri: to.path } });
    }
  }

  // 判断是否需要缓存页面
  handleKeepAlive(to, from, next);

  // 获取同步过来的cookie
  const mainToken = cookie.get(LOCAL_STORAGE_KEY.MAIN_TOKEN);
  // 判断是否有jm token 同步在cookie
  if (!checkLogin() && mainToken) {
    try {
      const data = await store.dispatch('jm/getUserInfoByToken', { token: mainToken });
      const { organizationId, webtoken, uid } = data.data;
      const brmAuthInfo = await store.dispatch('fetchUserTokenAndMenuAction', {
        goId: organizationId,
        token: webtoken,
        uid
      });
      storage.set(LOCAL_STORAGE_KEY.JM_AUTH_INFO, data.data, '', true);
      slsLogger.console({
        action: 'router',
        message: '无感登录：MAIN_TOKEN cookie 同步 JM_AUTH_INFO（未登录态）',
        extra: { toPath: to.path, toName: to.name, uid: data.data?.uid }
      });
      const { menus } = brmAuthInfo;
      const path = findFirstMenuPath(menus);
      window.parent && window.parent.postMessage({ event: 'author_success' }, '*');

      cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.comein.cn' });

      // 若是被 / redirect 兜底来的（redirectedFrom.path === '/'），则置空 intentPath 让位给默认菜单 path
      const intentPath = to.redirectedFrom?.path === '/' ? '' : to.fullPath;
      const finalRedirectUri = redirect_uri || intentPath || path || currentRedirectUrl;

      return next(finalRedirectUri);
    } catch (e) {
      logger.log('无感登录：MAIN_TOKEN cookie 同步 JM_AUTH_INFO（未登录态）失败', e);
      cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.comein.cn' });
      cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.finenter.ai' });
      // 自动登录失败，跳转到登录页并携带 redirect_uri
      return next({ name: 'login', query: { redirect_uri: to.fullPath || to.path } });
    }
  }

  // 判断是否有jm token 同步在cookie 并且是登录态，token不一致 (safari 兼容性问题)
  if (checkLogin() && mainToken) {
    const jmAuthUserInfo = getStorageAuthInfo(LOCAL_STORAGE_KEY.JM_AUTH_INFO, true);
    // token 不一致 已cookie的为准
    if (jmAuthUserInfo && jmAuthUserInfo?.webtoken !== mainToken) {
      try {
        const data = await store.dispatch('jm/getUserInfoByToken', { token: mainToken });
        const { organizationId, webtoken, uid } = data.data;
        const brmAuthInfo = await store.dispatch('fetchUserTokenAndMenuAction', {
          goId: organizationId,
          token: webtoken,
          uid
        });
        storage.set(LOCAL_STORAGE_KEY.JM_AUTH_INFO, data.data, '', true);
        slsLogger.console({
          action: 'router',
          message: '无感登录：MAIN_TOKEN 与本地 webtoken 不一致，以 cookie 为准同步 JM_AUTH_INFO',
          extra: { toPath: to.path, toName: to.name, uid: data.data?.uid }
        });

        window.parent && window.parent.postMessage({ event: 'author_success' }, '*');

        cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.comein.cn' });

        // 用户主动访问的页面保留原路由
        if (to.redirectedFrom?.path !== '/') return next();
        // / 被兜底重定向时，优先使用 redirect_uri，与未登录态分支保持一致
        if (redirect_uri) return next(redirect_uri);
        const newRootPath = findFirstMenuPath(brmAuthInfo?.menus || []) || currentRedirectUrl;
        return next({ path: newRootPath, query: to.query });
      } catch (e) {
        logger.log('无感登录：MAIN_TOKEN 与本地 webtoken 不一致，以 cookie 为准同步 JM_AUTH_INFO失败', e);
        cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.comein.cn' });
        cookie.remove(LOCAL_STORAGE_KEY.MAIN_TOKEN, { domain: '.finenter.ai' });
        return next();
      }
    }
  }

  if (to.name === 'login') {
    // 登录页面
    if (checkLogin()) {
      const menus = getOriginMenuKeyToValue().menus;
      return next(findFirstMenuPath(menus) || currentRedirectUrl);
    }
    // 会话已经结束，重新登录后要再拉一次用户信息
    init = false;
    // WorkBuddy 客户端里没有账号密码登录，落到登录页时自动走 wb.login 续登录态
    const workBuddyRedirectUri = redirect_uri || (isBusinessRoute(from) ? from.fullPath : '');
    const workBuddyLoginPageTarget = await resolveWorkBuddyLoginPageTarget(to, workBuddyRedirectUri);
    if (workBuddyLoginPageTarget) return next(workBuddyLoginPageTarget);
    return next();
  }
  // WorkBuddy 客户端打开的业务页缺少登录态时，先尝试用授权码静默登录，
  // 让已绑定的用户无感进入业务页，只有需要用户确认的场景才转授权页。
  if (!checkLogin() && openedFromWorkBuddy) {
    const workBuddyLoginStatus = await silentLoginFromWorkBuddy();
    // 重新发起导航，让菜单与权限校验按已登录态跑完整流程
    if (workBuddyLoginStatus === WORKBUDDY_LOGIN_STATUS.LOGGED_IN) return next(to.fullPath);
    // 未授权或未绑定 comein 账号：授权页承接授权与绑定，完成后按 redirect_uri 回到本页
    if (workBuddyLoginStatus === WORKBUDDY_LOGIN_STATUS.NEED_AUTHORIZE) {
      return next({ path: THIRD_PARTY_OAUTH_AUTHORIZE_PATH, query: { redirect_uri: to.fullPath } });
    }
    // UNAVAILABLE：非 WorkBuddy 环境或链路异常，已登录则沿用现有会话，未登录落到下方通用处理
  }

  // 未登录的情况跳转到登录页面
  if (!checkLogin()) {
    // WorkBuddy 客户端里没有账号密码登录入口，未登录一律回授权页重新授权绑定，不去通用登录页
    if (openedFromWorkBuddy) {
      return next({ path: THIRD_PARTY_OAUTH_AUTHORIZE_PATH, query: { redirect_uri: to.fullPath || to.path } });
    }
    if (isOverseasEnv()) {
      const redirectUrl = encodeURIComponent(
        ENV_DATA.VITE_PROXY_OVERSEA + window.location.pathname + window.location.search
      );
      location.href = `${ENV_DATA.VITE_OVERSEAS_LOGIN_URL}?redirect_uri=${redirectUrl}`;
    } else {
      return next({ name: 'login', query: { redirect_uri: to.fullPath || to.path } });
    }
  }

  if (!init) {
    // 获取用户信息
    await store.dispatch('fetchUserInfoAction');
    triggerSandboxInit(to);
    init = true;
  }

  // 判断页面是否有权限、没有跳转404页面 / // 白名单判断 不需要校验权限
  const menus = getOriginMenuKeyToValue().result;
  if (
    menus[to.path] ||
    menus[to.meta.parent] ||
    (redPaths[to.meta.parent] && menus[redPaths[to.meta.parent]]) ||
    (to.meta.requireAuth === false && !to.meta.thirdPartyPage) ||
    checkOtherParent(menus, to) ||
    whitePath.includes(to.path)
  ) {
    return next();
  }

  return next({ name: 'notFound' });
});

router.afterEach((to, from) => {
  const isNoAuthShareRoute = NO_AUTH_SHARE_ROUTE_NAMES.has(to.name);

  initVersionChecker();
  if (checkLogin() && !isNoAuthShareRoute) {
    // 重新登录成功后恢复版本检测：会话失效曾停止轮询，SPA 内重新登录不会重建检测器，
    // 需主动重置 authExpired 标记，否则版本检测会一直被拦截直至整页刷新
    versionChecker?.resetAuthState();
    triggerResearchWorkspaceInit(to);
  }

  // 检测版本更新
  checkVersionOnRoute(to, from);
  // 路由加载完成后，将路由加入缓存列表中
  if (to.path) {
    routeCache.addRoute(to.path);
  }
  if (isElectron) {
    to.meta?.keepAlive && store.commit('pushKeepAlive', to.meta?.componentName);
    // 一级页面才需要新增tab菜单 或者有electronSingle 或者路由声明了共用tab分组（如 AI进宝三模式）
    if (to.meta?.pageLevel === PAGE_LEVEL.FIRST || to.meta?.electronSingle || to.meta?.tabGroup)
      electron.dispatchEvent('router.push', to);
  }
});

// Loading CSS chunk 123 failed / Loading JS chunk 456 failed
// Loading chunk 3807 failed.\n(error: https://…/3807.xxx.js)
// Loading chunk src_components_xxx failed.(error:…)
const CHUNK_FAILED_REG = /Loading (?:CSS |JS )?chunk .+ failed/i;

router.onError((error) => {
  if (CHUNK_FAILED_REG.test(error.message)) {
    logger.log(error.message);
    ElMessage.error(i18n.global.t('common.tip.network_error'));
    return;
  }
  logger.error('路由导航异常:', error);
});

emitter.on('routerPush', (to) => {
  // electron 标签
  const promise = router.push(to.href);
  if (isElectron && to.title) {
    promise.then(() => {
      emitter.emit('set-tab-title', {
        title: to.title,
        fullPath: to.href
      });
    });
  }
});

export default router;
